import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { HomePage } from './pages/HomePage';
import { MachineActivationPage } from './pages/MachineActivationPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AccountPage } from './pages/AccountPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { TermsAndConditionsPage } from './pages/TermsAndConditionsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { AddCreditPage } from './pages/AddCreditPage';
import { HistoryPage } from './pages/HistoryPage';
import { SettingsPage } from './pages/SettingsPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { MachinesPage } from './pages/MachinesPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { MaintenanceEmployeeRoute } from './components/MaintenanceEmployeeRoute';
import { PartnerRoute } from './components/PartnerRoute';
import { PartnerDashboardPage } from './pages/PartnerDashboardPage';
import { SellerRoute } from './components/SellerRoute';
import { SellerDashboardPage } from './pages/SellerDashboardPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { SplashScreen } from './components/SplashScreen';
import { EmployeeManagementPage } from './pages/EmployeeManagementPage';
import { ScheduleManagementPage } from './pages/ScheduleManagementPage';
import { MySchedulePage } from './pages/MySchedulePage';
import { MaintenanceSessionPage } from './pages/MaintenanceSessionPage';
import { MaintenanceReportsPage } from './pages/MaintenanceReportsPage';
import { TvMapPage } from './pages/TvMapPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Skip the customer splash on the always-on TV/kiosk map route.
  const isKiosk = window.location.pathname.startsWith('/tv-map');
  const [showSplash, setShowSplash] = useState(!isKiosk);

  // Check if splash screen has been shown in this session
  useEffect(() => {
    const splashShown = sessionStorage.getItem('splashShown');
    if (splashShown) {
      setShowSplash(false);
    }
  }, []);

  const handleSplashComplete = () => {
    sessionStorage.setItem('splashShown', 'true');
    setShowSplash(false);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WebSocketProvider>
          {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
          <Router>
            <div className="min-h-screen bg-gray-50">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/machine/:code" element={<MachineActivationPage />} />
                <Route path="/tv-map" element={<TvMapPage />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route 
                  path="/account" 
                  element={
                    <ProtectedRoute>
                      <AccountPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/add-credit" 
                  element={
                    <ProtectedRoute>
                      <AddCreditPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/history" 
                  element={
                    <ProtectedRoute>
                      <HistoryPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/settings" 
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/subscription" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/machines" 
                  element={
                    <ProtectedRoute>
                      <MachinesPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <AdminRoute>
                      <AdminDashboardPage />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/employees" 
                  element={
                    <AdminRoute>
                      <EmployeeManagementPage />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/schedules" 
                  element={
                    <AdminRoute>
                      <ScheduleManagementPage />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/reports" 
                  element={
                    <AdminRoute>
                      <MaintenanceReportsPage />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/my-schedule" 
                  element={
                    <MaintenanceEmployeeRoute>
                      <MySchedulePage />
                    </MaintenanceEmployeeRoute>
                  } 
                />
                <Route
                  path="/maintenance-session/:sessionId"
                  element={
                    <MaintenanceEmployeeRoute>
                      <MaintenanceSessionPage />
                    </MaintenanceEmployeeRoute>
                  }
                />
                <Route
                  path="/parceiro"
                  element={
                    <PartnerRoute>
                      <PartnerDashboardPage />
                    </PartnerRoute>
                  }
                />
                <Route
                  path="/vendedor"
                  element={
                    <SellerRoute>
                      <SellerDashboardPage />
                    </SellerRoute>
                  }
                />
                <Route
                  path="/change-password"
                  element={
                    <ProtectedRoute>
                      <ChangePasswordPage />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </Router>
        </WebSocketProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App